import { useState, useEffect, lazy, Suspense, useContext } from 'react';
import { Route, Switch, Redirect, useHistory, useLocation } from 'react-router-dom';
import { Layout, Menu, Grid, Avatar, Form } from 'antd';
import { FolderOutlined, UsergroupAddOutlined, FileDoneOutlined, SettingOutlined } from '@ant-design/icons';
import LogoutIcon from '@mui/icons-material/Logout';
import cn from 'classnames';
import axios from 'axios';

import { useLogout } from '../../hooks/useLogout';
import { Login } from '../../pages/Login';
import { UserSettings } from '../UserSettings';
import { AppContext } from '../../context/context';
import { AppContextType } from '../../context/types';
import { setUserProfile, setUser } from '../../context/user/actions';
import {
  UserResourceControllerApiFactory as userAPI,
  UserDtoUserRoleEnum as UserRole,
  UserDto
} from '../../api_client/parameter_api';
import { getToken, setToken, insertTokenIntoHttp } from '../../utils/token';
import { getMinutesDiffs } from '../../utils/getMinutesDiffs';
import { apiLocation } from '../../config';
import { ModalType } from '../../types';
import { SessionModal } from '../SessionModal';
import { toggleUserSettings } from '../../context/user/actions';
import './App.less';

const { Sider } = Layout;
const { useBreakpoint } = Grid;
const Projects = lazy(() => import('../../pages/Projects/Projects'));
const Users = lazy(() => import('../../pages/Users/Users'));
const MasterLists = lazy(() => import('../../pages/MasterLists/MasterLists'));
const Project = lazy(() => import('../../pages/Project/Project'));
const MasterList = lazy(() => import('../../pages/MasterList/MasterList'));
const ProjectLog = lazy(() => import('../../pages/ProjectLog/ProjectLog'));

axios.interceptors.request.use(
  (config: any) => {
    if (config.url !== `${apiLocation}/api/users/refresh-token`) {
      const loggedUser = getToken();
      if (loggedUser?.token) {
        let jwt = loggedUser.token;
        const minutesDiffs = getMinutesDiffs();
        if (minutesDiffs <= 5) {
          userAPI(undefined, apiLocation)
            .refreshJwt({ url: apiLocation })
            .then(res => {
              jwt = res.data;
              setToken({ token: res.data, expires: res.headers.expires });
            });
        }
        config.headers['Authorization'] = `Bearer ${jwt}`;
      }
    }

    return config;
  },
  error => {
    Promise.reject(error);
  }
);

const App = () => {
  const [selectedKey, setSelectedKey] = useState<Array<string>>([]);
  const [state, dispatch] = useContext<any>(AppContext);
  const [sessionModal, setSessionModal] = useState<ModalType>({ visible: false, mode: 'none' });
  const [sessionModalInterval, setSessionModalInterval] = useState<NodeJS.Timeout | undefined>();

  const history = useHistory();
  const location = useLocation();
  const screens = useBreakpoint();
  const [userform] = Form.useForm();
  const logout = useLogout();

  axios.defaults.validateStatus = (status: number) => {
    if (status === 401) {
      logout();
      return true;
    }
    return status < 400;
  };

  const { siderIsCollapsed, userProfile, loggedUser, userSettings } = state as AppContextType;

  useEffect(() => {
    if (location.pathname !== '/') {
      const slashIndex = location.pathname.indexOf('/', 1);
      const currentKey = slashIndex === -1 ? location.pathname.slice(1) : location.pathname.slice(1, slashIndex);
      setSelectedKey([currentKey]);
    } else {
      history.push({
        pathname: 'projects',
        search: '?tab=active'
      });
      setSelectedKey(['projects']);
    }
    if (loggedUser) {
      insertTokenIntoHttp(loggedUser.token);
      userAPI(undefined, apiLocation)
        .getProfile()
        .then(res => {
          dispatch(setUserProfile(res.data));
          if (res.data?.passwordChangeRequired) {
            dispatch(toggleUserSettings({ visible: true, mode: 'edit', data: res.data }));
          }
        });
    } else {
      history.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userSettings.mode === 'edit') {
      userform.setFieldsValue({ ...userSettings.data });
      userform.setFields(Object.keys(userSettings.data).map(key => ({ name: key, touched: false })));
    } else {
      userform.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings.visible]);

  useEffect(() => {
    if (userProfile && !sessionModalInterval) {
      const intervalId = setInterval(() => {
        const minutesDiffs = getMinutesDiffs();
        if (minutesDiffs <= 3 && userProfile && !sessionModal.visible) {
          setSessionModal({ mode: 'none', visible: true });
        } else if (minutesDiffs < 0) {
          handleLogout();
        }
      }, 30000);
      setSessionModalInterval(intervalId);
      return () => clearInterval(intervalId);
    } else {
      if (sessionModalInterval) {
        clearInterval(sessionModalInterval!);
        setSessionModalInterval(undefined);
      }
    }
  }, [userProfile]);

  const handleMenuSelect = (item: any) => {
    let pathname = '';
    if (item.key === 'projects') {
      pathname = '/projects';
      history.push({ pathname, search: '?tab=active' });
    } else if (item.key === 'master-lists') {
      pathname = '/master-lists';
      history.push({ pathname });
    } else if (item.key === 'users') {
      pathname = '/users';
      history.push({ pathname, search: '?tab=active' });
    }
    setSelectedKey(item.key);
  };

  const handleConfirmLogin = (userData: { token: string; expires: string; user: UserDto }) => {
    dispatch(setUser({ token: userData.token, expires: userData.expires }));
    setSelectedKey(['projects']);
    history.push({
      pathname: '/projects',
      search: '?tab=active'
    });
  };

  const handleSessionModalOk = () => {
    setSessionModal({ ...sessionModal, visible: false });
  };

  const handleSessionModalCancel = () => {
    setSessionModal({ ...sessionModal, visible: false });
    logout();
  };

  const showUserSettings = () => {
    dispatch(toggleUserSettings({ visible: true, mode: 'edit', data: userProfile }));
  };

  const handleUserSettingsSave = () => {
    handleUserSettingsCancel();
  };

  const handleUserSettingsCancel = () => {
    dispatch(toggleUserSettings({ visible: false, mode: 'none' }));
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <>
      <Route path='/' exact>
        <Login handleConfirmLogin={handleConfirmLogin} />
      </Route>
      {loggedUser && (
        <Layout style={{ minHeight: '100vh' }}>
          <Sider
            collapsed={(state as AppContextType).siderIsCollapsed || !screens.md}
            width={260}
            trigger={null}
            className='app-sider'
          >
            <div className='app-logo-container'>
              <div className={cn({ logo: true, 'logo-responsive': siderIsCollapsed || !screens.md })}>PM</div>
              <div className='name' style={{ display: screens.md && !siderIsCollapsed ? 'block' : 'none' }}>
                Parameter Manager
              </div>
            </div>
            <Menu selectedKeys={selectedKey} onClick={handleMenuSelect} className='app-menu'>
              <Menu.Item className='app-menu-item' key='projects' icon={<FolderOutlined />}>
                Projects
              </Menu.Item>
              <Menu.Item
                className='app-menu-item'
                key='users'
                icon={<UsergroupAddOutlined />}
                style={{ display: userProfile?.userRole === UserRole.Admin ? 'block' : 'none' }}
              >
                Manage users
              </Menu.Item>
              <Menu.Item
                className='app-menu-item'
                key='master-lists'
                icon={<FileDoneOutlined />}
                style={{ display: userProfile?.userRole === UserRole.Admin ? 'block' : 'none' }}
              >
                Master lists
              </Menu.Item>
            </Menu>
            <div className='app-user-data'>
              {screens.md && !siderIsCollapsed ? (
                <>
                  <div style={{ width: '214px' }}>
                    <div className='app-user-name'>
                      {userProfile?.firstName} {userProfile?.lastName}
                    </div>
                    <div className='app-user-email'>{userProfile?.email}</div>
                  </div>
                  <SettingOutlined onClick={showUserSettings} style={{ paddingTop: '6px' }} />
                </>
              ) : (
                <span onClick={showUserSettings}>
                  <Avatar className='app-avatar' size='small'>
                    {userProfile?.firstName.charAt(0)}
                    {userProfile?.lastName?.charAt(0)}
                  </Avatar>
                </span>
              )}
            </div>

            <div
              className='app-logout-container'
              onClick={handleLogout}
              style={{ width: screens.md && !siderIsCollapsed ? '260px' : '80px' }}
            >
              <LogoutIcon />
              <div className='app-logout' style={{ display: screens.md && !siderIsCollapsed ? 'block' : 'none' }}>
                Logout
              </div>
            </div>
          </Sider>
          <Suspense fallback={<div>Loading...</div>}>
            <Switch>
              <Route path='/projects' exact>
                <Projects />
              </Route>
              <Route path='/users' exact>
                <Users />
              </Route>
              <Route path='/master-lists' exact>
                <MasterLists />
              </Route>
              <Route path='/projects/:id' exact>
                <Project />
              </Route>
              <Route path='/master-lists/:id' exact>
                <MasterList />
              </Route>
              <Route path='/projects/:id/log' exact>
                <ProjectLog />
              </Route>
              <Redirect to='/projects?tab=active' />
            </Switch>
          </Suspense>
        </Layout>
      )}
      <UserSettings
        form={userform}
        modal={userSettings}
        save={handleUserSettingsSave}
        cancel={handleUserSettingsCancel}
      />
      <SessionModal modal={sessionModal} ok={handleSessionModalOk} cancel={handleSessionModalCancel} />
    </>
  );
};

export default App;
